import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import ProfileCard from "@/components/Profile/ProfileCard";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { getUserProfile } from "./function";
import Spinner from "@/components/Spinner";

interface TabConfig {
  id: string;
  label: string;
  path: string;
}

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await getUserProfile();
        if (userData) {
          setUser(userData);
        }
      } catch (error) {
        // handle or log error
        // console.error('Failed to fetch user profile', error);
      }
    };

    fetchUser();
  }, []);

  // Mock user data
  const [user, setUser] = useState();

  const tabs: TabConfig[] = [
    {
      id: "investment",
      label: t("profile.tabs.myInvestment", "Đầu tư của tôi"),
      path: "/profile/investment",
    },
    {
      id: "edit",
      label: t("profile.tabs.editProfile", "Chỉnh sửa hồ sơ"),
      path: "/profile",
    },
    {
      id: "password",
      label: t("profile.tabs.changePassword", "Đổi mật khẩu"),
      path: "/profile/change-password",
    },
    // {
    //   id: "verification",
    //   label: t("profile.tabs.verification", "Xác minh danh tính"),
    //   path: "/profile/verification",
    // },
    {
      id: "banking",
      label: t("profile.tabs.bankAccount", "Tài khoản ngân hàng"),
      path: "/profile/banking",
    },
    {
      id: "documents",
      label: t("profile.tabs.documents", "Tài liệu"),
      path: "/profile/documents",
    },
  ];

  const getCurrentTab = React.useCallback(() => {
    if (location.pathname === "/profile/change-password") return "password";
    if (location.pathname === "/profile/investment") return "investment";
    if (location.pathname === "/profile/verification") return "verification";
    if (location.pathname === "/profile/banking") return "banking";
    if (location.pathname === "/profile/documents") return "documents";
    return "edit";
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState(getCurrentTab());

  useEffect(() => {
    setActiveTab(getCurrentTab());
  }, [getCurrentTab]);

  const handleTabChange = (tab: TabConfig) => {
    setActiveTab(tab.id);
    navigate(tab.path);
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case "investment":
        return t("profile.myInvestment", "Đầu tư của tôi");
      case "password":
        return t("profile.changePassword", "Đổi mật khẩu");
      case "verification":
        return t("profile.verification", "Xác minh danh tính");
      case "banking":
        return t("profile.bankAccount", "Tài khoản ngân hàng");
      case "documents":
        return t("profile.tabs.documents", "Tài liệu");
      default:
        return t("profile.viewProfile", "Xem hồ sơ");
    }
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: t("nav.dashboard", "Dashboard"), path: "/home" },
    { label: getPageTitle() },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={getPageTitle()} breadcrumbs={breadcrumbs} />

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-76 flex-shrink-0">
          <ProfileCard user={user} />
        </div>

        {/* Right Column */}
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-800 rounded-md shadow-sm overflow-hidden">
            {/* Tabs */}
            <div className="border-b border-gray-100 dark:border-gray-700 overflow-x-auto scrollbar-hide">
              <nav className="flex min-w-max">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab)}
                    className={`px-6 py-4 text-sm font-medium transition-colors relative whitespace-nowrap ${
                      activeTab === tab.id
                        ? "!text-primary dark:!text-primary-light"
                        : "!text-gray-500 dark:!text-gray-400 hover:!text-gray-700 dark:hover:!text-gray-300"
                    }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary dark:bg-primary-light" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              {user ? <Outlet context={{ user }} /> : <Spinner />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
